/**
 * Same-tab persistence for the `/do` action-link token.
 *
 * The token is stripped from the address bar at mount, so a reload would
 * otherwise lose it. `sessionStorage` is scoped to this tab and dies with it,
 * which keeps the token out of history, bookmarks and other tabs while a
 * reload or a back navigation still works. It is cleared once the link's job
 * is done or the server reports it dead.
 */
const STORAGE_KEY = "dearbill.actionLinkToken";

/** The `#action=<token>` fragment the texted link lands with, or null. */
function readFragmentToken(): string | null {
  return new URLSearchParams(window.location.hash.slice(1)).get("action");
}

/**
 * The token to use: a fragment token (a freshly opened link) wins and is
 * stored; otherwise the token this tab stored before a reload, or null.
 * Storage access can throw under strict privacy settings, which leaves the
 * fragment-only behavior.
 */
export function claimActionToken(): string | null {
  const fromFragment = readFragmentToken();
  try {
    if (fromFragment !== null) {
      window.sessionStorage.setItem(STORAGE_KEY, fromFragment);
      return fromFragment;
    }
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return fromFragment;
  }
}

/** Forget the stored token, once the link is used up or rejected. */
export function forgetActionToken(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing was stored if storage is unavailable.
  }
}
